/**
 * Complete the Look - Gemini 2 Upsell JavaScript
 * Handles mobile slider functionality with snap scrolling support
 * Performance optimized with passive event listeners and IntersectionObserver
 * 
 * @version 1.0.0
 * @size < 2KB minified
 */

(function() {
  'use strict';

  /**
   * Debounce function for performance
   */
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * UpsellSlider class
   */
  class UpsellSlider {
    constructor(container) {
      this.container = container;
      this.grid = container.querySelector('[data-upsell-grid]');
      
      if (!this.grid) return;

      this.cards = Array.from(this.grid.querySelectorAll('.gemini2-upsell-card'));
      this.currentIndex = 0;
      
      // Settings from data attributes
      this.snapEnabled = this.grid.dataset.snapEnabled === 'true';
      this.showArrows = this.grid.dataset.showArrows === 'true';
      this.showDots = this.grid.dataset.showDots === 'true';
      
      // UI elements
      this.prevArrow = container.querySelector('[data-upsell-arrow-prev]');
      this.nextArrow = container.querySelector('[data-upsell-arrow-next]');
      this.dotsContainer = container.querySelector('[data-upsell-dots]');
      this.dots = this.dotsContainer ? Array.from(this.dotsContainer.querySelectorAll('[data-upsell-dot]')) : [];
      
      this.init();
    }

    init() {
      // Only initialize on mobile
      if (window.innerWidth >= 750) return;
      
      this.setupEventListeners();
      this.setupIntersectionObserver();
      
      // Set initial active state
      this.updateActiveStates();
    }

    setupEventListeners() {
      // Arrow navigation
      if (this.prevArrow) {
        this.prevArrow.addEventListener('click', () => this.navigate('prev'));
      }
      
      if (this.nextArrow) {
        this.nextArrow.addEventListener('click', () => this.navigate('next'));
      }

      // Dot navigation
      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => this.scrollToIndex(index));
      });

      // Scroll event to update active states
      this.grid.addEventListener('scroll', debounce(() => {
        this.updateActiveStates();
      }, 100), { passive: true });

      // Touch events for better mobile UX
      let touchStartX = 0;
      let touchEndX = 0;

      this.grid.addEventListener('touchstart', (e) => {
        touchStartX = e.changedTouches[0].screenX;
      }, { passive: true });

      this.grid.addEventListener('touchend', (e) => {
        touchEndX = e.changedTouches[0].screenX;
        this.handleSwipe(touchStartX, touchEndX);
      }, { passive: true });

      // Keyboard navigation
      this.container.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.navigate('prev');
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.navigate('next');
        }
      });

      // Resize handler
      window.addEventListener('resize', debounce(() => {
        if (window.innerWidth >= 750) {
          this.destroy();
        }
      }, 250));
    }

    setupIntersectionObserver() {
      // Lazy load images that aren't in the initial viewport
      const imageObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const img = entry.target.querySelector('img[loading="lazy"]');
              if (img && !img.src) {
                img.src = img.dataset.src;
              }
              imageObserver.unobserve(entry.target);
            }
          });
        },
        {
          rootMargin: '50px'
        }
      );

      this.cards.forEach(card => {
        imageObserver.observe(card);
      });
    }

    handleSwipe(startX, endX) {
      const swipeThreshold = 50;
      const difference = startX - endX;

      if (Math.abs(difference) > swipeThreshold) {
        if (difference > 0) {
          // Swiped left
          this.navigate('next');
        } else {
          // Swiped right
          this.navigate('prev');
        }
      }
    }

    navigate(direction) {
      if (direction === 'prev' && this.currentIndex > 0) {
        this.currentIndex--;
      } else if (direction === 'next' && this.currentIndex < this.cards.length - 1) {
        this.currentIndex++;
      } else {
        return; // Already at boundary
      }

      this.scrollToIndex(this.currentIndex);
    }

    scrollToIndex(index) {
      if (index < 0 || index >= this.cards.length) return;

      this.currentIndex = index;
      const card = this.cards[index];

      if (!card) return;

      // Calculate scroll position
      const cardRect = card.getBoundingClientRect();
      const gridRect = this.grid.getBoundingClientRect();
      const scrollLeft = this.grid.scrollLeft;
      
      // Center the card
      const targetScroll = scrollLeft + (cardRect.left - gridRect.left) - (gridRect.width - cardRect.width) / 2;

      this.grid.scrollTo({
        left: targetScroll,
        behavior: 'smooth'
      });

      this.updateActiveStates();
    }

    updateActiveStates() {
      // Update current index based on scroll position
      const gridRect = this.grid.getBoundingClientRect();
      const gridCenter = gridRect.left + gridRect.width / 2;

      let closestIndex = 0;
      let closestDistance = Infinity;

      this.cards.forEach((card, index) => {
        const cardRect = card.getBoundingClientRect();
        const cardCenter = cardRect.left + cardRect.width / 2;
        const distance = Math.abs(cardCenter - gridCenter);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      this.currentIndex = closestIndex;

      // Update dots
      this.dots.forEach((dot, index) => {
        if (index === this.currentIndex) {
          dot.classList.add('active');
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.classList.remove('active');
          dot.removeAttribute('aria-current');
        }
      });

      // Update arrow states
      if (this.prevArrow) {
        this.prevArrow.disabled = this.currentIndex === 0;
        this.prevArrow.style.opacity = this.currentIndex === 0 ? '0.3' : '1';
      }

      if (this.nextArrow) {
        this.nextArrow.disabled = this.currentIndex === this.cards.length - 1;
        this.nextArrow.style.opacity = this.currentIndex === this.cards.length - 1 ? '0.3' : '1';
      }
    }

    destroy() {
      // Clean up event listeners
      if (this.prevArrow) {
        this.prevArrow.replaceWith(this.prevArrow.cloneNode(true));
      }
      if (this.nextArrow) {
        this.nextArrow.replaceWith(this.nextArrow.cloneNode(true));
      }
      this.dots.forEach(dot => {
        dot.replaceWith(dot.cloneNode(true));
      });
    }
  }

  /**
   * Initialize all upsell sliders on the page
   */
  function initUpsellSliders() {
    const containers = document.querySelectorAll('.gemini2-upsell-section');
    
    containers.forEach(container => {
      // Prevent double initialization
      if (container.dataset.upsellInitialized) return;
      
      container.dataset.upsellInitialized = 'true';
      new UpsellSlider(container);
    });
  }

  /**
   * Analytics tracking (optional)
   */
  function trackUpsellClick(productId, position) {
    // For Shopify Analytics
    if (window.ShopifyAnalytics) {
      window.ShopifyAnalytics.lib.track('Upsell Product Clicked', {
        productId: productId,
        position: position
      });
    }
  }

  /**
   * Setup analytics tracking on product clicks
   */
  function setupAnalytics() {
    document.querySelectorAll('.gemini2-upsell-card').forEach(card => {
      card.addEventListener('click', function() {
        const productId = this.dataset.productId;
        const position = this.dataset.upsellPosition;
        
        if (productId && position) {
          trackUpsellClick(productId, position);
        }
      }, { passive: true });
    });
  }

  /**
   * Initialize on DOM ready
   */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initUpsellSliders();
      setupAnalytics();
    });
  } else {
    initUpsellSliders();
    setupAnalytics();
  }

  /**
   * Re-initialize on Shopify section load (theme editor)
   */
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.querySelector('.gemini2-upsell-section')) {
      initUpsellSliders();
      setupAnalytics();
    }
  });

  /**
   * Handle section unload (cleanup)
   */
  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.querySelector('.gemini2-upsell-section');
    if (section) {
      delete section.dataset.upsellInitialized;
    }
  });

})();
